import {
  Card,
  CardNoteSide,
  CardRating,
  FSRSEngine,
  FSRSItemResult,
  Profile,
  ReviewLog,
  UserKnownWord,
  isRetired,
  isTranslationLanguage,
  mergeTranslations,
  sanitizeTranslations,
  withCardNote,
} from '@lingoping/core';
import { laterThan } from '../utils/timestamps.js';
import {
  CardContentFields,
  CardQueueFields,
  KnownWordInput,
  MAX_KNOWN_WORDS_PER_OP,
  SETTING_RANGES,
  SyncOp,
  SyncOpBody,
  SyncedSettings,
  cardContentOf,
  isDevMockUserId,
  newCardOf,
  newUuid,
  syncKeys,
} from './outbox.js';

/**
 * What is saved on this device.
 *
 * The server's copy of a learner's cards, known words, review history and settings is the real
 * one (Docs/SYNC_CONTRACT.md). What is kept here is a copy of it, to show things at once and to
 * work without a connection, plus the "outbox": the changes the learner made that the server has
 * not confirmed yet. Every method that changes something does two things, in this order and as
 * one step: it adds one operation to the outbox, then changes the saved copy. If the page is
 * closed in between, the operation is still sent and the server's answer puts the copy right.
 *
 * This module never talks to the network. services/syncEngine.ts sends the outbox and writes
 * what the server answers into the saved copy, using the plain setters here (`saveCards`,
 * `saveKnownWords`, `saveReviewLogs`, `saveProfile`, `saveOnboarding`). Those setters are for the
 * engine and for tests: they change this device's copy only, never the account.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Where values are kept when this is not the extension. By default `window.localStorage` (about
 * 5 MB); the website installs a bigger store with `setStorageBackend` before first use.
 */
export interface StorageBackend {
  get(key: string): Promise<unknown>;
  set(key: string, value: unknown): Promise<void>;
  /**
   * Runs `work` while nobody else using this store runs work under the same name. Optional: a
   * store without it is locked with the browser's Web Locks (see `StorageManager.locked`).
   */
  lock?<T>(name: string, work: () => Promise<T>): Promise<T>;
}

let storageBackend: StorageBackend | null = null;

export function setStorageBackend(backend: StorageBackend | null): void {
  storageBackend = backend;
}

const STORAGE_KEYS = {
  PROFILE: 'lingoping_profile',
  CARDS: 'lingoping_cards',
  KNOWN_WORDS: 'lingoping_known_words',
  REVIEW_LOGS: 'lingoping_review_logs',
  INITIALIZED: 'lingoping_initialized',
  CURRENT_USER_ID: 'lingoping_current_user_id',
  SCHEMA_VERSION: 'lingoping_schema_version',
  ONBOARDING: 'lingoping_onboarding',
};

/**
 * Bump when locally stored learning data must be reset once. v2 removed the built-in seed
 * cards/known words: existing local decks are wiped a single time so everyone restarts at onboarding.
 */
const SCHEMA_VERSION = 2;

const WIPE_PREFIXES = [
  STORAGE_KEYS.CARDS,
  STORAGE_KEYS.KNOWN_WORDS,
  STORAGE_KEYS.REVIEW_LOGS,
  STORAGE_KEYS.ONBOARDING,
  'lingoping_visual_cache',
];

const DEFAULT_PROFILE: Profile = {
  id: 'local-user-1',
  native_language: 'en',
  target_language: 'de',
  session_interval_minutes: 10,
  cards_per_session: 5,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

/** The account's settings on a device that has not heard from the server (yet, or any more). */
const DEFAULT_SETTINGS = {
  native_language: DEFAULT_PROFILE.native_language,
  session_interval_minutes: DEFAULT_PROFILE.session_interval_minutes,
  cards_per_session: DEFAULT_PROFILE.cards_per_session,
  new_words_per_day: undefined,
};

/** The settings as they can be sent: unknown languages and non-numbers are left out. */
function cleanSettings(settings: SyncedSettings): SyncedSettings {
  const clean: SyncedSettings = {};
  if (isTranslationLanguage(settings.native_language)) {
    clean.native_language = settings.native_language;
  }
  for (const key of Object.keys(SETTING_RANGES) as (keyof typeof SETTING_RANGES)[]) {
    const value = settings[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const [min, max] = SETTING_RANGES[key];
    clean[key] = Math.min(max, Math.max(min, Math.round(value)));
  }
  return clean;
}

/** JSON with its keys in a fixed order, to tell whether two values say the same. */
function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : 1));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

/** Whether two cards have the same text and picture (a missing detail and an empty one count as the same). */
function sameContent(a: Card, b: Card): boolean {
  const comparable = (card: Card) => {
    const content = cardContentOf(card);
    return stableJson({
      ...content,
      linguistic_meta: content.linguistic_meta ?? {},
      visual_meta: content.visual_meta ?? {},
    });
  };
  return comparable(a) === comparable(b);
}

/** Work waiting for a lock, per lock name, where the browser has no Web Locks. */
const lockQueues = new Map<string, Promise<unknown>>();

const scheduler = new FSRSEngine();

/** An operation to add to the outbox; `id` only where the caller needs to know it. */
type NewOp = SyncOpBody & { id?: string };
type Send = (op: NewOp) => Promise<void>;

/**
 * Robust Cross-Environment Storage Wrapper
 * Works seamlessly in Chrome Extension MV3 (chrome.storage.local)
 * and in standard browser dev environments (localStorage).
 */
export class StorageManager {
  private static memoryStore = new Map<string, any>();
  private static outboxListeners = new Set<() => void>();

  /** Tells the sync engine that this page added to the outbox. Returns an unsubscribe function. */
  public static onOutboxChange(listener: () => void): () => void {
    this.outboxListeners.add(listener);
    return () => this.outboxListeners.delete(listener);
  }

  private static isChromeStorageAvailable(): boolean {
    return typeof chrome !== 'undefined' && !!chrome.storage && !!chrome.storage.local;
  }

  private static async getRaw<T>(key: string): Promise<T | null> {
    if (storageBackend) return ((await storageBackend.get(key)) as T) || null;

    if (this.isChromeStorageAvailable()) {
      const res = await chrome.storage.local.get(key);
      return (res[key] as T) || null;
    }

    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      typeof window.localStorage.getItem === 'function'
    ) {
      try {
        const item = window.localStorage.getItem(key);
        return item ? (JSON.parse(item) as T) : null;
      } catch {
        // Fallback to memoryStore
      }
    }

    return this.memoryStore.has(key) ? (this.memoryStore.get(key) as T) : null;
  }

  private static async setRaw<T>(key: string, value: T): Promise<void> {
    if (storageBackend) {
      await storageBackend.set(key, value);
      return;
    }

    if (this.isChromeStorageAvailable()) {
      await chrome.storage.local.set({ [key]: value });
      return;
    }

    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      typeof window.localStorage.setItem === 'function'
    ) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
        return;
      } catch {
        // Fallback to memoryStore
      }
    }

    this.memoryStore.set(key, value);
  }

  private static async removeKeysWithPrefix(prefixes: string[]): Promise<void> {
    const matches = (key: string) => prefixes.some((p) => key.startsWith(p));

    if (this.isChromeStorageAvailable()) {
      const all = await chrome.storage.local.get(null);
      const doomed = Object.keys(all).filter(matches);
      if (doomed.length > 0) await chrome.storage.local.remove(doomed);
      return;
    }

    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      typeof window.localStorage.getItem === 'function'
    ) {
      try {
        const doomed: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const key = window.localStorage.key(i);
          if (key && matches(key)) doomed.push(key);
        }
        doomed.forEach((k) => window.localStorage.removeItem(k));
      } catch {
        // Fall through to memoryStore
      }
    }

    for (const key of Array.from(this.memoryStore.keys())) {
      if (matches(key)) this.memoryStore.delete(key);
    }
  }

  /**
   * Runs `work` while no other page, and not the background worker, runs work under the same
   * name on this device's storage. The dashboard, the popup, the review window and the worker
   * all read and write the same saved lists; without this, two of them changing a list at the
   * same moment would each write their own version and one change would be gone.
   *
   * `data` guards every read-change-write of the saved copy and the outbox (short, storage
   * only: never wait for the network inside it). `sync` is held by the one page or worker that
   * is talking to the server (services/syncEngine.ts).
   *
   * Uses the browser's Web Locks, which every context of the extension (and every tab of the
   * website) shares and which the browser releases when a page or the worker dies. A lock is
   * not re-entrant: work under `data` must not ask for `data` again.
   */
  public static locked<T>(name: 'data' | 'sync', work: () => Promise<T>): Promise<T> {
    const lockName = `lingoping-${name}`;
    if (storageBackend?.lock) return storageBackend.lock(lockName, work);
    // `self` is the page or the worker this runs in; anything else is not a browser.
    const locks = typeof self !== 'undefined' ? self.navigator?.locks : undefined;
    if (locks?.request) return locks.request(lockName, work) as Promise<T>;
    // No Web Locks (a test environment): one page at least never runs two pieces at once.
    const run = (lockQueues.get(lockName) ?? Promise.resolve()).then(work);
    lockQueues.set(
      lockName,
      run.catch(() => undefined)
    );
    return run;
  }

  /**
   * One change by the learner: `work` adds its operation with `send` and then changes the saved
   * copy, all under the `data` lock. The sync engine is told afterwards.
   */
  private static async change<T>(work: (send: Send) => Promise<T>): Promise<T> {
    let added = false;
    const result = await this.locked('data', () =>
      work(async (op) => {
        added = (await this.addToOutbox(op)) || added;
      })
    );
    if (added) {
      for (const listener of this.outboxListeners) {
        try {
          listener();
        } catch (err) {
          console.warn('[LingoPing Storage] An outbox listener threw:', err);
        }
      }
    }
    return result;
  }

  /**
   * Appends an operation to the signed-in learner's outbox. Nothing is kept for the simulated
   * development account or when nobody is signed in: there is no server to send it to, and the
   * saved copy is all there is.
   */
  private static async addToOutbox(op: NewOp): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId || isDevMockUserId(userId)) return false;
    const key = syncKeys(userId).outbox;
    const outbox = (await this.getRaw<SyncOp[]>(key)) || [];
    outbox.push({ ...op, id: op.id ?? newUuid(), at: new Date().toISOString() } as SyncOp);
    await this.setRaw(key, outbox);
    return true;
  }

  /** The operations the server has not answered yet, oldest first. */
  public static async getOutbox(userId?: string | null): Promise<SyncOp[]> {
    const id = userId ?? (await this.getCurrentUserId());
    if (!id) return [];
    return (await this.getRaw<SyncOp[]>(syncKeys(id).outbox)) || [];
  }

  /**
   * Adds an operation to the outbox without touching the saved copy: for a change whose result
   * is on this device already (services/legacyMerge.ts hands an old installation's own data to
   * the server this way). Everything a learner does goes through the methods further down.
   */
  public static async enqueue(op: SyncOpBody): Promise<void> {
    await this.change((send) => send(op));
  }

  private static activeUserId: string | null = null;

  /**
   * Who is signed in on this device. Each page remembers the answer; `fresh` asks storage again
   * (another page may have signed out meanwhile).
   */
  public static async getCurrentUserId(options: { fresh?: boolean } = {}): Promise<string | null> {
    if (this.activeUserId && !options.fresh) return this.activeUserId;
    const stored = await this.getRaw<string>(STORAGE_KEYS.CURRENT_USER_ID);
    this.activeUserId = stored || null;
    return this.activeUserId;
  }

  public static async setCurrentUserId(userId: string | null): Promise<void> {
    this.activeUserId = userId;
    if (userId) {
      await this.setRaw(STORAGE_KEYS.CURRENT_USER_ID, userId);
    } else {
      await this.setRaw(STORAGE_KEYS.CURRENT_USER_ID, null);
    }
  }

  /** Forgets who is signed in. The learner's saved data is removed by `clearUserData`. */
  public static async onUserLoggedOut(): Promise<void> {
    await this.setCurrentUserId(null);
  }

  /**
   * Removes everything of one learner that the sync keeps on this device: cards, known words,
   * review history, the onboarding mark, the outbox and the place the last pull reached. The
   * account's settings go back to their defaults. Used at sign-out, after the outbox was sent
   * (or the learner agreed to lose what could not be sent): nothing of it is needed again, the
   * server has the real copy.
   */
  public static async clearUserData(userId: string): Promise<void> {
    await this.locked('data', async () => {
      const keys = syncKeys(userId);
      await this.setRaw(this.getUserCardsKey(userId), null);
      await this.setRaw(`${STORAGE_KEYS.ONBOARDING}_${userId}`, null);
      await this.setRaw(keys.outbox, null);
      await this.setRaw(keys.state, null);
      for (const key of [STORAGE_KEYS.KNOWN_WORDS, STORAGE_KEYS.REVIEW_LOGS]) {
        const rows = (await this.getRaw<{ user_id: string }[]>(key)) || [];
        const others = rows.filter((row) => row.user_id !== userId);
        if (others.length !== rows.length) await this.setRaw(key, others.length ? others : null);
      }
      const profile = await this.getRaw<Profile>(STORAGE_KEYS.PROFILE);
      if (profile) await this.setRaw(STORAGE_KEYS.PROFILE, { ...profile, ...DEFAULT_SETTINGS });
    });
  }

  /**
   * Ensures a default profile exists and runs the one-time local data reset when the stored
   * schema version is older than SCHEMA_VERSION. The auth session and profile settings are kept.
   */
  public static async initSeedData(): Promise<void> {
    const version = (await this.getRaw<number>(STORAGE_KEYS.SCHEMA_VERSION)) || 1;
    if (version < SCHEMA_VERSION) {
      await this.removeKeysWithPrefix(WIPE_PREFIXES);
      await this.setRaw(STORAGE_KEYS.SCHEMA_VERSION, SCHEMA_VERSION);
    }

    const initialized = await this.getRaw<boolean>(STORAGE_KEYS.INITIALIZED);
    if (!initialized) {
      const existingProfile = await this.getRaw<Profile>(STORAGE_KEYS.PROFILE);
      if (!existingProfile) await this.setRaw(STORAGE_KEYS.PROFILE, DEFAULT_PROFILE);
      await this.setRaw(STORAGE_KEYS.INITIALIZED, true);
    }
  }

  public static async getProfile(): Promise<Profile> {
    await this.initSeedData();
    const profile = await this.getRaw<Profile>(STORAGE_KEYS.PROFILE);
    // The "own Gemini key for a natural voice" setting was removed (v3.1.0): a key saved by an
    // older version is deleted from the device instead of being left lying around.
    if (profile && 'google_api_key' in profile) {
      delete (profile as Profile & { google_api_key?: string }).google_api_key;
      await this.setRaw(STORAGE_KEYS.PROFILE, profile);
    }
    const userId = await this.getCurrentUserId();
    if (userId && profile) {
      return { ...profile, id: userId };
    }
    return profile || DEFAULT_PROFILE;
  }

  /**
   * Writes the profile saved on this device. This does not reach the account: the settings a
   * learner changes go through `saveSettings`.
   */
  public static async saveProfile(profile: Partial<Profile>): Promise<Profile> {
    const current = await this.getProfile();
    const userId = (await this.getCurrentUserId()) || current.id;
    const updated: Profile = {
      ...current,
      ...profile,
      id: userId,
      updated_at: new Date().toISOString(),
    };
    await this.setRaw(STORAGE_KEYS.PROFILE, updated);
    return updated;
  }

  /**
   * Saves settings of the account (translation language, new words per day, reminder interval,
   * cards per review): here at once, and on the server with the next sync (`settings.set`),
   * from where the learner's other devices pick them up. Only what really changed is sent.
   */
  public static async saveSettings(settings: SyncedSettings): Promise<Profile> {
    const clean = cleanSettings(settings);
    return this.change(async (send) => {
      const current = await this.getProfile();
      const changed: SyncedSettings = {};
      for (const key of Object.keys(clean) as (keyof SyncedSettings)[]) {
        if (clean[key] !== current[key]) Object.assign(changed, { [key]: clean[key] });
      }
      if (Object.keys(changed).length === 0) return current;
      await send({ type: 'settings.set', ...changed });
      return this.saveProfile(changed);
    });
  }

  /**
   * Small device-local values that are not part of the profile or the synced data (retry
   * backoff state, the sync engine's own notes). Works in the extension and in the dev page like
   * everything else here.
   */
  public static async getLocalValue<T>(key: string): Promise<T | null> {
    return this.getRaw<T>(key);
  }

  public static async setLocalValue<T>(key: string, value: T): Promise<void> {
    await this.setRaw(key, value);
  }

  private static getUserCardsKey(userId: string | null): string {
    return userId ? `${STORAGE_KEYS.CARDS}_${userId}` : STORAGE_KEYS.CARDS;
  }

  public static async getCards(): Promise<Card[]> {
    await this.initSeedData();
    const userId = await this.getCurrentUserId();
    if (userId) {
      const userCards = await this.getRaw<Card[]>(this.getUserCardsKey(userId));
      return userCards || [];
    }
    const cards = await this.getRaw<Card[]>(STORAGE_KEYS.CARDS);
    return cards || [];
  }

  /**
   * Replaces the cards saved on this device. For the sync engine (writing what the server
   * answered) and for tests: nothing written this way reaches the account.
   */
  public static async saveCards(cards: Card[]): Promise<void> {
    const userId = await this.getCurrentUserId();
    await this.setRaw(this.getUserCardsKey(userId), cards);
  }

  /**
   * Changes one saved card and sends the operation `op` describes. `next` gets the card as it
   * is saved now (never a copy a screen has been holding) and answers the changed card, or null
   * when there is nothing to change. Returns the card as it is saved afterwards, or null when
   * the card is not on this device (deleted here or on another device).
   */
  private static async changeCard(
    cardId: string,
    next: (current: Card) => Card | null,
    op: NewOp
  ): Promise<Card | null> {
    return this.change(async (send) => {
      const cards = await this.getCards();
      const index = cards.findIndex((c) => c.id === cardId);
      if (index === -1) return null;
      const current = cards[index];
      const changed = next(current);
      if (!changed) return current;
      const stamped: Card = { ...changed, updated_at: laterThan(current.updated_at) };
      await send(op);
      cards[index] = stamped;
      await this.saveCards(cards);
      return stamped;
    });
  }

  /** A new card: saved here and added to the account (`card.add`). */
  public static async addCard(card: Card): Promise<void> {
    await this.change(async (send) => {
      const userId = await this.getCurrentUserId();
      if (userId && !card.user_id) {
        card.user_id = userId;
      }
      await send({ type: 'card.add', card: newCardOf(card) });
      const cards = await this.getCards();
      cards.unshift(card);
      await this.saveCards(cards);
    });
  }

  /**
   * Changes the text or picture of a card (`card.content`). Only the fields given are touched;
   * translations are added to the ones the card has. The card's reviews, notes, retirement and
   * place in "Up next" cannot be changed this way.
   */
  public static async updateCardContent(
    cardId: string,
    content: CardContentFields
  ): Promise<Card | null> {
    const given = Object.fromEntries(
      Object.entries(content).filter(([, value]) => value !== undefined)
    ) as CardContentFields;
    return this.changeCard(
      cardId,
      (current) => {
        const { translations, ...fields } = given;
        const next = { ...current } as Card & Record<string, unknown>;
        for (const [key, value] of Object.entries(fields)) next[key] = value ?? undefined;
        if (translations) {
          next.translations = mergeTranslations(
            current.translations,
            sanitizeTranslations(translations)
          );
        }
        return sameContent(current, next) ? null : next;
      },
      { type: 'card.content', card_id: cardId, content: given }
    );
  }

  /**
   * Saves the text and picture of `card`. Kept for callers that hold a whole card; whatever else
   * differs in their copy (reviews, notes, retirement, "Up next") is ignored: each of those has
   * its own method.
   */
  public static async updateCard(card: Card): Promise<Card | null> {
    return this.updateCardContent(card.id, cardContentOf(card));
  }

  /**
   * One answer in a review: the card is rescheduled here at once, the review is added to the
   * history, and the rating is sent (`card.review`). The server schedules its own copy with the
   * same rules; its result replaces this one when it answers (they differ only when another
   * device reviewed the card in between).
   */
  public static async applyReview(
    card: Card,
    rating: CardRating,
    reviewedAt: Date = new Date()
  ): Promise<FSRSItemResult> {
    return this.change(async (send) => {
      const userId = (await this.getCurrentUserId()) || 'local-user-1';
      const cards = await this.getCards();
      const index = cards.findIndex((c) => c.id === card.id);
      const current = index === -1 ? card : cards[index];
      // Like the server: a card's time never runs backwards.
      const last = current.last_review ? Date.parse(current.last_review) : 0;
      const scheduledAt = new Date(Math.max(reviewedAt.getTime(), last || 0));
      const scheduled = scheduler.schedule(current, rating, scheduledAt);
      // The review's id is the operation's: sent twice, it is still one review.
      const id = newUuid();
      const reviewLog: ReviewLog = {
        ...scheduled.reviewLog,
        id,
        user_id: userId,
        reviewed_at: reviewedAt.toISOString(),
      };
      const updated: Card = { ...scheduled.card, updated_at: laterThan(current.updated_at) };
      // Deleted meanwhile (here or on another device): there is no card to review any more.
      if (index === -1) return { card: updated, reviewLog };

      await send({
        id,
        type: 'card.review',
        card_id: card.id,
        rating,
        reviewed_at: reviewLog.reviewed_at,
      });
      cards[index] = updated;
      await this.saveCards(cards);
      await this.saveReviewLogs([reviewLog, ...(await this.getReviewLogs())]);
      return { card: updated, reviewLog };
    });
  }

  /** "I already know this word" (`retiredAt`) and its undo (null): `card.retire`. */
  public static async setCardRetired(
    cardId: string,
    retiredAt: string | null
  ): Promise<Card | null> {
    return this.changeCard(
      cardId,
      (current) =>
        (current.retired_at ?? null) === retiredAt ? null : { ...current, retired_at: retiredAt },
      { type: 'card.retire', card_id: cardId, retired_at: retiredAt }
    );
  }

  /** The learner's note on one side of a card; an empty text removes it (`card.note`). */
  public static async setCardNote(
    cardId: string,
    side: CardNoteSide,
    text: string
  ): Promise<Card | null> {
    return this.changeCard(
      cardId,
      (current) => {
        const next = withCardNote(current, side, text, laterThan(current.notes_updated_at));
        const same =
          (next.note_front ?? null) === (current.note_front ?? null) &&
          (next.note_back ?? null) === (current.note_back ?? null);
        return same ? null : next;
      },
      { type: 'card.note', card_id: cardId, side, text }
    );
  }

  /** A card's place in "Up next to learn" and the deck it was chosen in (`card.queue`). */
  public static async setCardQueue(cardId: string, fields: CardQueueFields): Promise<Card | null> {
    const given = Object.fromEntries(
      Object.entries(fields).filter(([, value]) => value !== undefined)
    ) as CardQueueFields;
    return this.changeCard(
      cardId,
      (current) => {
        const next = { ...current, ...given };
        const same =
          (next.queued_at ?? null) === (current.queued_at ?? null) &&
          (next.queue_priority ?? 0) === (current.queue_priority ?? 0) &&
          (next.source_deck_id ?? null) === (current.source_deck_id ?? null);
        return same ? null : next;
      },
      { type: 'card.queue', card_id: cardId, ...given }
    );
  }

  /** Removes a card for good, here and in the account (`card.delete`). Its reviews stay. */
  public static async deleteCard(cardId: string): Promise<void> {
    await this.change(async (send) => {
      await send({ type: 'card.delete', card_id: cardId });
      const cards = await this.getCards();
      const filtered = cards.filter((c) => c.id !== cardId);
      if (filtered.length !== cards.length) await this.saveCards(filtered);
    });
  }

  public static async getKnownWords(): Promise<UserKnownWord[]> {
    await this.initSeedData();
    const userId = await this.getCurrentUserId();
    const words = (await this.getRaw<UserKnownWord[]>(STORAGE_KEYS.KNOWN_WORDS)) || [];
    if (userId) {
      return words.filter((w) => w.user_id === userId);
    }
    return words;
  }

  /**
   * Replaces the signed-in learner's known words saved on this device (another account's words
   * on the same device are kept). For the sync engine and for tests, like `saveCards`.
   */
  public static async saveKnownWords(words: UserKnownWord[]): Promise<void> {
    const userId = await this.getCurrentUserId();
    const all = (await this.getRaw<UserKnownWord[]>(STORAGE_KEYS.KNOWN_WORDS)) || [];
    const others = userId ? all.filter((w) => w.user_id !== userId) : [];
    await this.setRaw(STORAGE_KEYS.KNOWN_WORDS, [...words, ...others]);
  }

  /** Adds one known word (its own group unless `baseLemma` says otherwise). */
  public static async addKnownWord(
    lemma: string,
    language: string = 'de',
    baseLemma?: string
  ): Promise<void> {
    await this.addKnownForms(baseLemma ?? lemma, [lemma], language);
  }

  /**
   * Adds every form of a word to the known list in one step, all sharing `baseLemma`
   * (`known.add`). Forms the learner knows already are left out.
   */
  public static async addKnownForms(
    baseLemma: string,
    forms: string[],
    language: string = 'de'
  ): Promise<void> {
    await this.change(async (send) => {
      const userId = (await this.getCurrentUserId()) || 'local-user-1';
      const words = await this.getKnownWords();
      const existing = new Set(words.map((w) => w.lemma.toLowerCase()));

      const newlyAdded: UserKnownWord[] = [];
      for (const form of forms) {
        const trimmed = form.trim();
        const key = trimmed.toLowerCase();
        if (!trimmed || existing.has(key)) continue;
        existing.add(key);
        newlyAdded.push({
          id: newUuid(),
          user_id: userId,
          lemma: trimmed,
          language,
          base_lemma: baseLemma,
          created_at: new Date().toISOString(),
        });
      }
      if (newlyAdded.length === 0) return;

      // One operation, unless there are more forms than the server takes in one.
      for (let i = 0; i < newlyAdded.length; i += MAX_KNOWN_WORDS_PER_OP) {
        const batch = newlyAdded.slice(i, i + MAX_KNOWN_WORDS_PER_OP);
        await send({
          type: 'known.add',
          words: batch.map((w): KnownWordInput => ({
            id: w.id,
            lemma: w.lemma,
            language: w.language,
            base_lemma: w.base_lemma ?? null,
            created_at: w.created_at,
          })),
        });
      }
      await this.saveKnownWords([...newlyAdded.reverse(), ...words]);
    });
  }

  /** Removes one known form (`known.remove`). */
  public static async removeKnownWord(id: string): Promise<void> {
    await this.change(async (send) => {
      const words = await this.getKnownWords();
      const filtered = words.filter((w) => w.id !== id);
      if (filtered.length === words.length) return;
      await send({ type: 'known.remove', word_id: id });
      await this.saveKnownWords(filtered);
    });
  }

  /**
   * Removes every form of a word at once (used by the grouped Lexicon and by swipe undo):
   * `known.remove_group`, one per language the word is known in (German, today).
   */
  public static async removeKnownGroup(baseLemma: string): Promise<void> {
    await this.change(async (send) => {
      const base = baseLemma.trim();
      const words = await this.getKnownWords();
      const inGroup = (w: UserKnownWord) =>
        (w.base_lemma ?? w.lemma).toLowerCase() === base.toLowerCase();
      const languages = new Set(words.filter(inGroup).map((w) => w.language));
      // Sent even if nothing matched here: the account may hold forms this device never saw.
      if (languages.size === 0) languages.add((await this.getProfile()).target_language || 'de');
      for (const language of languages) {
        await send({ type: 'known.remove_group', base_lemma: base, language });
      }
      const filtered = words.filter((w) => !inGroup(w));
      if (filtered.length !== words.length) await this.saveKnownWords(filtered);
    });
  }

  /**
   * Older known words could have non-UUID local ids (`kw_...`), which the server refuses. Run
   * once, before an old installation's words are handed to the server (services/legacyMerge.ts).
   */
  public static async normalizeKnownWordIds(): Promise<void> {
    const words = (await this.getRaw<UserKnownWord[]>(STORAGE_KEYS.KNOWN_WORDS)) || [];
    let changed = false;
    for (const w of words) {
      if (!UUID_RE.test(w.id)) {
        w.id = newUuid();
        changed = true;
      }
    }
    if (changed) await this.setRaw(STORAGE_KEYS.KNOWN_WORDS, words);
  }

  /**
   * Everything that must NOT be offered again from a deck/file/subtitle: all known forms plus the
   * lemma and every form of each card the user is already learning. Lowercased.
   */
  public static async getKnownFilterSet(): Promise<Set<string>> {
    const [known, cards] = await Promise.all([this.getKnownWords(), this.getCards()]);
    const set = new Set<string>();
    for (const w of known) set.add(w.lemma.toLowerCase());
    for (const c of cards) {
      if (c.lemma) set.add(c.lemma.toLowerCase());
      for (const form of c.linguistic_meta?.forms ?? []) set.add(form.toLowerCase());
    }
    return set;
  }

  public static async isOnboardingComplete(): Promise<boolean> {
    await this.initSeedData();
    const userId = (await this.getCurrentUserId()) || 'local-user-1';
    return Boolean(await this.getRaw<string>(`${STORAGE_KEYS.ONBOARDING}_${userId}`));
  }

  /**
   * Writes this device's note of when onboarding was finished (null = not finished). For the
   * sync engine and for tests, like `saveCards`; the learner finishing it is `completeOnboarding`.
   */
  public static async saveOnboarding(completedAt: string | null): Promise<void> {
    const userId = (await this.getCurrentUserId()) || 'local-user-1';
    await this.setRaw(`${STORAGE_KEYS.ONBOARDING}_${userId}`, completedAt);
  }

  /** The learner finished onboarding: noted here and in the account (`settings.set`). */
  public static async completeOnboarding(): Promise<void> {
    await this.change(async (send) => {
      await send({ type: 'settings.set', onboarding_completed: true });
      await this.saveOnboarding(new Date().toISOString());
    });
  }

  public static async getReviewLogs(): Promise<ReviewLog[]> {
    const userId = await this.getCurrentUserId();
    const logs = (await this.getRaw<ReviewLog[]>(STORAGE_KEYS.REVIEW_LOGS)) || [];
    if (userId) {
      return logs.filter((l) => l.user_id === userId);
    }
    return logs;
  }

  /**
   * Replaces the signed-in learner's review history saved on this device, newest first. For the
   * sync engine and for tests, like `saveCards`; a review the learner makes is `applyReview`.
   */
  public static async saveReviewLogs(logs: ReviewLog[]): Promise<void> {
    const userId = await this.getCurrentUserId();
    const all = (await this.getRaw<ReviewLog[]>(STORAGE_KEYS.REVIEW_LOGS)) || [];
    const others = userId ? all.filter((l) => l.user_id !== userId) : [];
    await this.setRaw(STORAGE_KEYS.REVIEW_LOGS, [...logs, ...others]);
  }

  /**
   * Counts for the popup, the badge and the options page. Retired cards ("I already know this
   * word") are not counted. A word that waits in "Up next to learn" is a card but not "due".
   */
  public static async getStats(): Promise<{
    totalCards: number;
    dueCards: number;
    knownWordsCount: number;
    reviewsToday: number;
  }> {
    const allCards = await this.getCards();
    const cards = allCards.filter((c) => !isRetired(c));
    const known = await this.getKnownWords();
    const logs = await this.getReviewLogs();

    const nowIso = new Date().toISOString();
    const todayStr = nowIso.slice(0, 10);

    const dueCards = cards.filter((c) => c.fsrs_state !== 'New' && c.due_date <= nowIso).length;

    const reviewsToday = logs.filter((l) => l.reviewed_at.startsWith(todayStr)).length;

    return {
      totalCards: cards.length,
      dueCards,
      knownWordsCount: known.length,
      reviewsToday,
    };
  }
}

export { isDevMockUserId } from './outbox.js';
